import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { productsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
    Package,
    Search,
    Plus,
    Upload,
    MoreVertical,
    Edit,
    Trash2,
    RefreshCw,
    FileSpreadsheet,
    ImageIcon,
    AlertCircle,
    FolderOpen,
    X,
    Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Products = () => {
    const { user } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCollection, setSelectedCollection] = useState('all');
    const [categories, setCategories] = useState([]);

    // Dialog states
    const [productDialog, setProductDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [importDialog, setImportDialog] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [importing, setImporting] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        prix: '',
        prix_achat: '',
        sku: '',
        image_url: '',
        images: [],
        category: '',
        description: '',
        stock_quantity: ''
    });
    
    // Image upload state
    const [uploadingImages, setUploadingImages] = useState(false);
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);

    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (searchQuery) params.search = searchQuery;
            if (selectedCollection && selectedCollection !== 'all') params.category = selectedCollection;

            const response = await productsAPI.getAll(params);
            setProducts(response.data);
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Erreur lors du chargement des produits');
        } finally {
            setLoading(false);
        }
    }, [searchQuery, selectedCollection]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await productsAPI.getCategories();
            // Handle both formats: array or {categories: array}
            const cats = response.data?.categories || response.data || [];
            setCategories(Array.isArray(cats) ? cats : []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, [fetchProducts, fetchCategories]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchProducts();
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery, selectedCollection, fetchProducts]);

    const openNewProductDialog = () => {
        setSelectedProduct(null);
        setFormData({
            name: '',
            prix: '',
            prix_achat: '',
            sku: '',
            image_url: '',
            images: [],
            category: '',
            description: '',
            stock_quantity: ''
        });
        setImageFiles([]);
        setImagePreviews([]);
        setProductDialog(true);
    };

    const openEditDialog = (product) => {
        setSelectedProduct(product);
        // Filter out empty image strings
        const existingImages = (product.images || []).filter(img => img && img.trim() !== '');
        setFormData({
            name: product.name || '',
            prix: product.prix?.toString() || '',
            prix_achat: product.prix_achat?.toString() || '',
            sku: product.sku || '',
            image_url: product.image_url || '',
            images: existingImages,
            category: product.category || '',
            description: product.description || '',
            stock_quantity: product.stock_quantity?.toString() || ''
        });
        // Set existing images as previews
        setImageFiles([]);
        setImagePreviews(existingImages);
        setProductDialog(true);
    };

    // Handle image file selection
    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files);
        const currentCount = imagePreviews.length;
        const remainingSlots = 5 - currentCount;
        
        if (files.length > remainingSlots) {
            toast.error(`Vous ne pouvez ajouter que ${remainingSlots} image(s) supplémentaire(s)`);
            return;
        }
        
        // Validate file types
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));
        if (invalidFiles.length > 0) {
            toast.error('Types de fichiers non supportés. Utilisez: JPG, PNG, WebP, GIF');
            return;
        }
        
        // Validate file sizes (max 5MB each)
        const oversizedFiles = files.filter(f => f.size > 5 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            toast.error('Certaines images dépassent 5MB');
            return;
        }
        
        // Create previews
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setImageFiles(prev => [...prev, ...files]);
        setImagePreviews(prev => [...prev, ...newPreviews]);
    };

    // Remove an image
    const removeImage = (index) => {
        const preview = imagePreviews[index];
        
        // Check if it's an existing image URL or a blob preview
        if (preview.startsWith('blob:')) {
            // It's a new file, remove from imageFiles too
            const fileIndex = index - (imagePreviews.length - imageFiles.length);
            if (fileIndex >= 0) {
                setImageFiles(prev => prev.filter((_, i) => i !== fileIndex));
            }
            URL.revokeObjectURL(preview);
        } else {
            // It's an existing image, remove from formData.images
            setFormData(prev => ({
                ...prev,
                images: prev.images.filter((_, i) => i !== index)
            }));
        }
        
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setUploadingImages(true);
            
            // Upload new images if any
            let uploadedUrls = [];
            if (imageFiles.length > 0) {
                const uploadFormData = new FormData();
                imageFiles.forEach(file => {
                    uploadFormData.append('files', file);
                });
                
                const uploadResponse = await productsAPI.uploadImages(uploadFormData);
                uploadedUrls = uploadResponse.data.urls;
            }
            
            // Combine existing images with newly uploaded ones
            const existingImages = formData.images || [];
            const allImages = [...existingImages, ...uploadedUrls];
            
            const data = {
                ...formData,
                prix: parseFloat(formData.prix) || 0,
                prix_achat: parseFloat(formData.prix_achat) || 0,
                stock_quantity: parseInt(formData.stock_quantity) || 0,
                images: allImages,
                image_url: allImages[0] || formData.image_url || '' // First image as main
            };

            if (selectedProduct) {
                await productsAPI.update(selectedProduct.id, data);
                toast.success('Produit mis à jour');
            } else {
                await productsAPI.create(data);
                toast.success('Produit créé');
            }

            setProductDialog(false);
            setImageFiles([]);
            setImagePreviews([]);
            fetchProducts();
        } catch (error) {
            console.error('Error saving product:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setUploadingImages(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedProduct) return;
        try {
            await productsAPI.delete(selectedProduct.id);
            toast.success('Produit supprimé');
            setDeleteDialog(false);
            fetchProducts();
        } catch (error) {
            console.error('Error deleting product:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const handleFileImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setImporting(true);
            const response = await productsAPI.importFromExcel(file);
            toast.success(
                `Import terminé: ${response.data.imported} nouveaux, ${response.data.updated} mis à jour`
            );
            if (response.data.errors?.length > 0) {
                toast.warning(`${response.data.errors.length} erreurs détectées`);
            }
            setImportDialog(false);
            fetchProducts();
        } catch (error) {
            console.error('Error importing products:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
        } finally {
            setImporting(false);
            e.target.value = '';
        }
    };

    const handleShopifyImport = async () => {
        try {
            setImporting(true);
            const response = await productsAPI.importFromShopify();
            toast.success(
                `Import Shopify: ${response.data.imported} nouveaux, ${response.data.updated} mis à jour`
            );
            setImportDialog(false);
            fetchProducts();
        } catch (error) {
            console.error('Error importing from Shopify:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'import Shopify');
        } finally {
            setImporting(false);
        }
    };

    const handleResetAndSync = async () => {
        if (!window.confirm('⚠️ Attention!\n\nCette action va:\n1. Supprimer TOUS les produits\n2. Réimporter depuis Shopify avec les Collections\n\nContinuer?')) {
            return;
        }
        
        try {
            setImporting(true);
            const response = await productsAPI.resetAndSyncShopify();
            toast.success(
                `Reset terminé: ${response.data.deleted} supprimés, ${response.data.imported} importés`
            );
            setImportDialog(false);
            fetchProducts();
            fetchCategories();
        } catch (error) {
            console.error('Error resetting products:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors du reset');
        } finally {
            setImporting(false);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-DZ', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price || 0) + ' DA';
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Package className="h-6 w-6" />
                            Catalogue Produits
                        </h1>
                        <p className="text-muted-foreground">
                            {products.length} produit{products.length > 1 ? 's' : ''} 
                            {selectedCollection !== 'all' && ` dans "${selectedCollection}"`}
                        </p>
                    </div>

                    {isAdmin && (
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setImportDialog(true)}>
                                <Upload className="h-4 w-4 mr-2" />
                                Importer
                            </Button>
                            <Button onClick={openNewProductDialog}>
                                <Plus className="h-4 w-4 mr-2" />
                                Nouveau Produit
                            </Button>
                        </div>
                    )}
                </div>

                {/* Search Bar */}
                <div className="flex gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Rechercher par nom ou SKU..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <Button variant="outline" onClick={fetchProducts}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>

                {/* Collections Tabs */}
                <Tabs value={selectedCollection} onValueChange={setSelectedCollection} className="w-full">
                    <TabsList className="w-full h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
                        <TabsTrigger 
                            value="all" 
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                        >
                            <FolderOpen className="h-4 w-4 mr-1" />
                            Tous
                        </TabsTrigger>
                        {categories.map((cat) => (
                            <TabsTrigger 
                                key={cat} 
                                value={cat}
                                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs sm:text-sm"
                            >
                                {cat.length > 25 ? cat.substring(0, 25) + '...' : cat}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>

                {/* Products Grid */}
                {loading ? (
                    <div className="flex items-center justify-center p-12">
                        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
                    </div>
                ) : products.length === 0 ? (
                    <Card>
                        <CardContent className="flex flex-col items-center justify-center p-12 text-muted-foreground">
                            <Package className="h-12 w-12 mb-4 opacity-50" />
                            <p className="text-lg font-medium">Aucun produit trouvé</p>
                            <p className="text-sm">
                                {searchQuery || selectedCollection !== 'all'
                                    ? 'Essayez de modifier vos filtres'
                                    : 'Commencez par ajouter un produit ou importer depuis Excel'}
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                        {products.map((product) => (
                            <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow group">
                                {/* Product Image */}
                                <div className="aspect-square bg-muted relative">
                                    {product.image_url ? (
                                        <img
                                            src={product.image_url}
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                    ) : null}
                                    <div className={`absolute inset-0 flex items-center justify-center ${product.image_url ? 'hidden' : ''}`}>
                                        <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
                                    </div>
                                    
                                    {/* Category Badge */}
                                    {product.category && (
                                        <span className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium bg-black/70 text-white">
                                            {product.category}
                                        </span>
                                    )}
                                    
                                    {/* Admin Actions */}
                                    {isAdmin && (
                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="secondary" size="icon" className="h-8 w-8">
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => openEditDialog(product)}>
                                                        <Edit className="h-4 w-4 mr-2" />
                                                        Modifier
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        className="text-destructive"
                                                        onClick={() => {
                                                            setSelectedProduct(product);
                                                            setDeleteDialog(true);
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4 mr-2" />
                                                        Supprimer
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Product Info */}
                                <CardContent className="p-3">
                                    <h3 className="font-medium text-sm line-clamp-2 mb-1" title={product.name}>
                                        {product.name}
                                    </h3>
                                    {product.sku && (
                                        <p className="text-xs text-muted-foreground mb-2">{product.sku}</p>
                                    )}
                                    <div className="flex items-center justify-between">
                                        <span className="font-bold text-primary">
                                            {formatPrice(product.prix)}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                            (product.stock_quantity || 0) > 10
                                                ? 'bg-green-100 text-green-700'
                                                : (product.stock_quantity || 0) > 0
                                                ? 'bg-yellow-100 text-yellow-700'
                                                : 'bg-red-100 text-red-700'
                                        }`}>
                                            {product.stock_quantity || 0}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Product Dialog */}
                <Dialog open={productDialog} onOpenChange={setProductDialog}>
                    <DialogContent className="sm:max-w-[500px]">
                        <DialogHeader>
                            <DialogTitle>
                                {selectedProduct ? 'Modifier le produit' : 'Nouveau produit'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit}>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="name">Nom du produit *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="grid gap-2">
                                        <Label htmlFor="prix">Prix de vente (DA) *</Label>
                                        <Input
                                            id="prix"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={formData.prix}
                                            onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label htmlFor="prix_achat">Prix d'achat (DA)</Label>
                                        <Input
                                            id="prix_achat"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={formData.prix_achat}
                                            onChange={(e) => setFormData({ ...formData, prix_achat: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="grid gap-2">
                                        <Label htmlFor="sku">SKU / Référence</Label>
                                        <Input
                                            id="sku"
                                            value={formData.sku}
                                            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                                        />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label htmlFor="stock">Stock</Label>
                                        <Input
                                            id="stock"
                                            type="number"
                                            min="0"
                                            value={formData.stock_quantity}
                                            onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="category">Catégorie</Label>
                                    <Input
                                        id="category"
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        placeholder="ex: Parfum Homme, Parfum Femme..."
                                    />
                                </div>

                                {/* Image Upload Section */}
                                <div className="grid gap-2">
                                    <Label>Images du produit (max 5)</Label>
                                    <div className="border-2 border-dashed border-border rounded-lg p-4">
                                        {/* Image Previews */}
                                        {imagePreviews.length > 0 && (
                                            <div className="grid grid-cols-5 gap-2 mb-4">
                                                {imagePreviews.map((preview, index) => {
                                                    // Handle different image URL formats
                                                    let imgSrc = preview;
                                                    if (!preview.startsWith('blob:') && !preview.startsWith('http')) {
                                                        // Relative path - prefix with API URL
                                                        imgSrc = `${API_URL}${preview}`;
                                                    }
                                                    return (
                                                        <div key={index} className="relative group">
                                                            <img
                                                                src={imgSrc}
                                                                alt={`Preview ${index + 1}`}
                                                                className="w-full h-16 object-cover rounded border bg-slate-100"
                                                                onError={(e) => {
                                                                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => removeImage(index)}
                                                                className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                            {index === 0 && (
                                                                <span className="absolute bottom-0 left-0 right-0 text-[9px] bg-primary text-white text-center">
                                                                    Principal
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        
                                        {/* Upload Button */}
                                        {imagePreviews.length < 5 && (
                                            <label className="flex flex-col items-center justify-center cursor-pointer py-4 hover:bg-muted/50 rounded transition-colors">
                                                <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                                                <span className="text-sm text-muted-foreground">
                                                    Cliquez pour ajouter des images
                                                </span>
                                                <span className="text-xs text-muted-foreground mt-1">
                                                    JPG, PNG, WebP, GIF • Max 5MB par image
                                                </span>
                                                <input
                                                    type="file"
                                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                                    multiple
                                                    onChange={handleImageSelect}
                                                    className="hidden"
                                                />
                                            </label>
                                        )}
                                        
                                        {imagePreviews.length >= 5 && (
                                            <p className="text-sm text-center text-muted-foreground py-2">
                                                Maximum 5 images atteint
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="description">Description</Label>
                                    <Input
                                        id="description"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    />
                                </div>
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setProductDialog(false)}>
                                    Annuler
                                </Button>
                                <Button type="submit" disabled={uploadingImages}>
                                    {uploadingImages ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Upload en cours...
                                        </>
                                    ) : (
                                        selectedProduct ? 'Mettre à jour' : 'Créer'
                                    )}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-destructive">
                                <AlertCircle className="h-5 w-5" />
                                Confirmer la suppression
                            </DialogTitle>
                        </DialogHeader>
                        <p>
                            Êtes-vous sûr de vouloir supprimer <strong>{selectedProduct?.name}</strong> ?
                            Cette action est irréversible.
                        </p>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
                                Annuler
                            </Button>
                            <Button variant="destructive" onClick={handleDelete}>
                                Supprimer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Import Dialog */}
                <Dialog open={importDialog} onOpenChange={setImportDialog}>
                    <DialogContent className="sm:max-w-[450px]">
                        <DialogHeader>
                            <DialogTitle>Importer des produits</DialogTitle>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            {/* Excel Import */}
                            <Card className="p-4">
                                <div className="flex items-start gap-4">
                                    <FileSpreadsheet className="h-8 w-8 text-green-600 shrink-0" />
                                    <div className="flex-1">
                                        <h3 className="font-semibold">Import Excel/CSV</h3>
                                        <p className="text-sm text-muted-foreground mb-3">
                                            Colonnes: Name/Nom, Prix, Prix Achat, SKU, Category, Stock
                                        </p>
                                        <input
                                            type="file"
                                            accept=".csv,.xlsx,.xls"
                                            onChange={handleFileImport}
                                            className="hidden"
                                            id="file-upload"
                                            disabled={importing}
                                        />
                                        <label htmlFor="file-upload">
                                            <Button
                                                variant="outline"
                                                className="w-full"
                                                disabled={importing}
                                                asChild
                                            >
                                                <span>
                                                    {importing ? (
                                                        <>
                                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                            Import en cours...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Upload className="h-4 w-4 mr-2" />
                                                            Sélectionner un fichier
                                                        </>
                                                    )}
                                                </span>
                                            </Button>
                                        </label>
                                    </div>
                                </div>
                            </Card>

                            {/* Shopify Import */}
                            <Card className="p-4">
                                <div className="flex items-start gap-4">
                                    <Package className="h-8 w-8 text-purple-600 shrink-0" />
                                    <div className="flex-1">
                                        <h3 className="font-semibold">Import Shopify</h3>
                                        <p className="text-sm text-muted-foreground mb-3">
                                            Synchroniser tous les produits de votre boutique Shopify
                                        </p>
                                        <Button
                                            variant="outline"
                                            className="w-full"
                                            onClick={handleShopifyImport}
                                            disabled={importing}
                                        >
                                            {importing ? (
                                                <>
                                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                    Import en cours...
                                                </>
                                            ) : (
                                                <>
                                                    <RefreshCw className="h-4 w-4 mr-2" />
                                                    Synchroniser avec Shopify
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </Card>

                            {/* Reset & Sync Shopify */}
                            <Card className="p-4 border-red-200 bg-red-50">
                                <div className="flex items-start gap-4">
                                    <Trash2 className="h-8 w-8 text-red-600 shrink-0" />
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-red-700">Reset & Sync Shopify</h3>
                                        <p className="text-sm text-red-600 mb-3">
                                            ⚠️ Supprime TOUS les produits et réimporte depuis Shopify avec les Collections
                                        </p>
                                        <Button
                                            variant="destructive"
                                            className="w-full"
                                            onClick={handleResetAndSync}
                                            disabled={importing}
                                        >
                                            {importing ? (
                                                <>
                                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                    Reset en cours...
                                                </>
                                            ) : (
                                                <>
                                                    <Trash2 className="h-4 w-4 mr-2" />
                                                    Vider & Resynchroniser
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setImportDialog(false)}>
                                Fermer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default Products;
